import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";
import {LoggedInLayout} from "features/layouts/pages/logged-in";
import {connect} from "react-redux";
import {LoadingLogoPortal} from "features/components/loading-logo";
import {setSingleVideoProgress, startSingleVideo} from "features/pages/single-video/reducer";
import {singleVideoData, singleVideoErrorSelector, singleVideoLoading} from "features/pages/single-video/selectors";
import {withToastManager} from "react-toast-notifications";
import {__} from "services/i18n";
import Title from "features/components/title";
import Text from "features/components/text";
import {historyObject} from "historyObject";
import {favoriteVideoStart} from "store/reducer/video/favorite/reducer";
import {Icon} from "features/components/icon";
import {MaterialBox} from "features/components/material-box";
import ReactPlayer from 'react-player/vimeo';
import {VIDEO_PROGRESS_INTERVAL_MS, MOBILE_MEDIA_QUERY_THRESHOLD} from "constants.js";
import {LikeBox} from "features/components/like-box";
import {likeVideoStart} from "store/reducer/video/like/reducer";
import {likeVideoErrorSelector} from "store/reducer/video/like/selectors";
import CommentField from "features/components/comment-field";
import CommentBox from "features/components/comment-box";
import {commentEditShowModal} from "features/modals/comment-edit/selectors";
import {ResourcePaymentSection} from "features/components/resource-payment-section";
import SuggestedResource from "features/components/suggested";
import {useMediaQuery} from "services/utils/utils";
import {SpeakerBox} from "features/components/speakers";
import {CategoryTagBox} from "features/components/category-tag-box";
import {ReportAProblemButton} from "features/components/problem-button";


/* istanbul ignore next */
const mapStateToProps = state => ({
    video: singleVideoData(state),
    error: singleVideoErrorSelector(state),
    loading: singleVideoLoading(state),
    like_error: likeVideoErrorSelector(state),
    should_show_edit_modal: commentEditShowModal(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    startSingleVideo,
    favoriteVideoStart,
    setSingleVideoProgress,
    likeVideoStart
};
export const SingleVideoPage = (props) => {
    const {video, error, loading, like_error, startSingleVideo, favoriteVideoStart, setSingleVideoProgress, likeVideoStart, should_show_edit_modal} = props;

    React.useEffect(() => {
        startSingleVideo({id: props.match.params.id})
    }, [props.match.params.id, startSingleVideo]);

    React.useEffect(() => {
        if(!error.inError) return;
        props.toastManager.add(__(error.error), { appearance: 'error', id: 'error-load-single-video-toast'});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);
    React.useEffect(() => {
        if(!like_error.inError) return;
        props.toastManager.add(__(like_error.error), { appearance: 'error', id: 'error-like-video-toast'});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [like_error]);

    const [favorite, setFavorite] = useState((video || {}).favorite);

    React.useEffect(() => {
        if(!video) return;
        setFavorite(video.favorite);
    }, [video]);

    const toggleFavorite = useCallback((event) => {
        setFavorite(!favorite);
        favoriteVideoStart({id: video.id, favorite: !video.favorite});
        event.preventDefault();
        event.stopPropagation();
    }, [favorite, video, favoriteVideoStart]);

    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_THRESHOLD);

    const breadcrumbTitle = useMemo(() => {
        if (isMobile) return "single-video.breadcrumb";
        return video ? video.title : "single-video.breadcrumb"
    }, [video, isMobile]);


    const renderVideoData = useCallback(() => {
        return <>
            <div className="row favorite-name">
                <div className="name">
                    {video.requires_payment && <Icon>lock</Icon>}
                    <Text size={16}>{__(video.has_directory ? "single-video.directory" : "single-video.single")}</Text>
                    {video.has_directory && <div className="directory-link" onClick={() => historyObject.push(`/directory/${video.directory.id}`)}><Text size={16}>{video.directory.name}</Text></div>}
                </div>
                <div className="favorite-btn" onClick={toggleFavorite}>
                    <Icon>{favorite ? "heart-fill": "heart"}</Icon>
                </div>
            </div>
            <Title size={34}>{video.title}</Title>
            <CategoryTagBox categories={video.categories} tags={video.tags} />
            {video.speakers && <SpeakerBox speakers={video.speakers} />}
            <span className="separator" />
            </>
    }, [favorite, toggleFavorite, video]);

    const renderSidebar = useCallback(() => {
        return <div className="sidebar">
            {!isMobile && renderVideoData()}
            {video.description && <div className="description">
                <Title size={24}>{__("single-video.description.title")}</Title>
                <Text size={16}>{video.description}</Text>
            </div>}
            {(video.material && video.material.length > 0) && <MaterialBox data={video.material} locked={video.requires_payment} />}
            <ReportAProblemButton />
        </div>
    }, [isMobile, renderVideoData, video]);

    const videoProgressCallback = useCallback((event) => {
        setSingleVideoProgress({id: video.id, progress: event.playedSeconds});
    }, [setSingleVideoProgress, video]);

    const videoPlayer = React.useRef(null);

    const goToPayment = useCallback(() => {
        historyObject.push(`/payment/video/${video.id}`);
    }, [video]);

    const toggleVideoLike = useCallback(() => {
        likeVideoStart({id: video.id, like: !video.like});
    }, [likeVideoStart, video]);


    const renderContent = useCallback(() => {
        if(loading) {
           return <LoadingLogoPortal />;
        }
        if(!video) {
            return <Text size={16} align={"center"}>{__("single-video.not-found")}</Text>
        }
        return <><div className="sidebar-wrapper">
            <div className="content">
                <div className="video">
                    {!video.requires_payment && <ReactPlayer
                        ref={videoPlayer}
                        url={`https://player.vimeo.com/video/${video.video_id}`}
                        width={"100%"}
                        height={"100%"}
                        onProgress={videoProgressCallback}
                        progressInterval={VIDEO_PROGRESS_INTERVAL_MS}
                        controls={true}
                        onReady={() => videoPlayer.current.player.seekTo(video.progress)}
                    />}
                    <ResourcePaymentSection data={video} goToPayment={goToPayment} type={"video"} />
                </div>
                {video.has_directory && <div className="directory-nav">
                    <div
                        className={`directory-nav-item ${video.directory_index === 0 ? "hide" : ""}`}
                        onClick={() => historyObject.push(`/video/${video.directory.videos[video.directory_index - 1].id}`)}
                    >
                        <Icon>arrow-circle-left</Icon>
                        <Title size={14}>{__("buttons.previous-item")}</Title>
                    </div>
                    <div className="directory-nav-item" onClick={() => historyObject.push(`/directory/${video.directory.id}`)}>
                        <Title size={14}>{__("buttons.show-directory")}</Title>
                        <Icon>dashboard</Icon>
                    </div>
                    <div
                        className={`directory-nav-item ${video.directory_index === video.directory.video_number - 1 ? "hide" : ""}`}
                        onClick={() => historyObject.push(`/video/${video.directory.videos[video.directory_index + 1].id}`)}
                    >
                        <Title size={14}>{__("buttons.next-item")}</Title>
                        <Icon>arrow-circle-right</Icon>
                    </div>
                </div>}
                {isMobile && renderVideoData()}
                {!video.requires_payment && <div className={"like-comment"}>
                    <LikeBox onLike={toggleVideoLike} total={video.likes.total} like={video.like} avatars={video.likes.avatars} />
                    {!should_show_edit_modal && <CommentField type={"video"} resource_id={video.id} />}
                </div>}
                <CommentBox type={"video"} resource_id={video.id} />
            </div>
            {renderSidebar()}
        </div>
        {<SuggestedResource resource_id={video.id} type={"video"} />}
        </>;
    }, [goToPayment, isMobile, loading, renderSidebar, renderVideoData, should_show_edit_modal, toggleVideoLike, video, videoProgressCallback]);



    return <LoggedInLayout
        type={"light"}
        className={"single-video-page"}
        breadcrumbs={[{title: "common.home-breadcrumb", link:"/"}, {title: "videos.breadcrumb", link: "/videos"}, {title: breadcrumbTitle}]}>
        {renderContent()}
    </LoggedInLayout>
};
export default connect(mapStateToProps, mapDispatchToProps)(withToastManager(SingleVideoPage));